'use client';

import { Box, Button, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';

const GOLD = '#FFC107';
const SURFACE_DEEP = '#0a0a0a';
const ON_MUTED = 'rgba(255,255,255,0.45)';
const RADIUS = '4px';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    /** Obsidian Gold bar + numbered pages (Buzz Bridge). Default keeps theme-driven outline buttons. */
    variant?: 'default' | 'obsidian';
}

function buildPageItems(current: number, total: number): (number | 'ellipsis')[] {
    if (total < 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i);
    }

    const items: (number | 'ellipsis')[] = [1];
    if (range.length && range[0] > 2) items.push('ellipsis');
    range.forEach(p => items.push(p));
    if (range.length && range[range.length - 1] < total - 1) items.push('ellipsis');
    if (total > 1) items.push(total);
    return items;
}

export function Pagination({ currentPage, totalPages, onPageChange, variant = 'default' }: PaginationProps) {
    const { themeStyle } = useThemeContext();
    const textColor = theme[themeStyle].tag_white;
    const borderColor = textColor;
    const hoverBg = themeStyle === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';

    const safeTotal = Math.max(1, totalPages || 1);
    const safePage = Math.min(Math.max(1, currentPage), safeTotal);
    const pageItems = buildPageItems(safePage, safeTotal);

    if (variant === 'obsidian') {
        const prevDisabled = safePage <= 1;
        const nextDisabled = safePage >= safeTotal;

        return (
            <Box
                component="footer"
                sx={{
                    mt: 2,
                    px: { xs: 2, sm: 3 },
                    py: 3,
                    bgcolor: SURFACE_DEEP,
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: { xs: 0, sm: `${RADIUS}` },
                    border: { sm: '1px solid rgba(255,255,255,0.05)' },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                    minWidth: 0,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        letterSpacing: '0.06em',
                        color: ON_MUTED,
                    }}>
                    Page{' '}
                    <Box component="span" sx={{ color: '#fff' }}>
                        {safePage}
                    </Box>{' '}
                    of{' '}
                    <Box component="span" sx={{ color: '#fff' }}>
                        {safeTotal}
                    </Box>
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                    }}>
                    <Button
                        size="small"
                        disabled={prevDisabled}
                        onClick={() => onPageChange(safePage - 1)}
                        startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            px: 2,
                            py: 1,
                            minWidth: 'auto',
                            borderRadius: RADIUS,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: prevDisabled ? ON_MUTED : ON_MUTED,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            '&:hover': {
                                bgcolor: prevDisabled ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
                                borderColor: prevDisabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,201,101,0.25)',
                            },
                            '&.Mui-disabled': {
                                color: ON_MUTED,
                                borderColor: 'rgba(255,255,255,0.08)',
                                opacity: 0.45,
                            },
                        }}>
                        Previous
                    </Button>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mx: { xs: 0, sm: 1 },
                        }}>
                        {pageItems.map((item, idx) =>
                            item === 'ellipsis' ? (
                                <Typography
                                    key={`e-${idx}`}
                                    sx={{
                                        px: 0.5,
                                        color: ON_MUTED,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                    }}>
                                    …
                                </Typography>
                            ) : (
                                <Button
                                    key={item}
                                    onClick={() => onPageChange(item)}
                                    disableRipple
                                    sx={{
                                        minWidth: 32,
                                        width: 32,
                                        height: 32,
                                        p: 0,
                                        borderRadius: RADIUS,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        color: item === safePage ? '#0a0a0a' : ON_MUTED,
                                        bgcolor: item === safePage ? GOLD : 'transparent',
                                        border: item === safePage ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                        '&:hover': {
                                            bgcolor: item === safePage ? '#ffd54f' : 'rgba(255,201,101,0.08)',
                                            borderColor: 'rgba(255,201,101,0.25)',
                                            color: item === safePage ? '#0a0a0a' : GOLD,
                                        },
                                    }}>
                                    {item}
                                </Button>
                            ),
                        )}
                    </Box>

                    <Button
                        size="small"
                        disabled={nextDisabled}
                        onClick={() => onPageChange(safePage + 1)}
                        endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            px: 2,
                            py: 1,
                            minWidth: 'auto',
                            borderRadius: RADIUS,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: nextDisabled ? ON_MUTED : GOLD,
                            bgcolor: nextDisabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,201,101,0.06)',
                            border: nextDisabled
                                ? '1px solid rgba(255,255,255,0.08)'
                                : '1px solid rgba(255,201,101,0.22)',
                            '&:hover': {
                                bgcolor: nextDisabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,201,101,0.12)',
                                borderColor: nextDisabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,201,101,0.35)',
                            },
                            '&.Mui-disabled': {
                                color: ON_MUTED,
                                borderColor: 'rgba(255,255,255,0.08)',
                                opacity: 0.45,
                            },
                        }}>
                        Next
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2" sx={{ color: textColor }}>
                Page {safePage} of {safeTotal}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
                    onClick={() => onPageChange(safePage - 1)}
                    disabled={safePage <= 1}
                    sx={{
                        color: textColor,
                        borderColor,
                        '&:hover': {
                            backgroundColor: hoverBg,
                        },
                        '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                    }}>
                    Previous
                </Button>

                <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                    onClick={() => onPageChange(safePage + 1)}
                    disabled={safePage >= safeTotal}
                    sx={{
                        color: textColor,
                        borderColor,
                        '&:hover': {
                            backgroundColor: hoverBg,
                        },
                        '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                    }}>
                    Next
                </Button>
            </Box>
        </Box>
    );
}
